import { Group } from 'src/design/atoms/Group';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { ContactDto } from 'src/api/FiretrolApiClient';
import { useContactsByCustomer } from 'src/api/queries/organization/useContactsByCustomer';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { Header } from 'src/design/organisms/Header';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';
import { useJobs } from 'src/hooks/useJobs';
import { CreateContactDialog } from 'src/routes/Settings/Contacts/components/CreateContactDialog';

const getColumns = (): MRT_ColumnDef<ContactDto>[] => {
  return [
    {
      accessorKey: 'firstName',
      header: 'First',
    },
    {
      accessorKey: 'lastName',
      header: 'Last',
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
    },
    {
      accessorKey: 'email',
      header: 'Email',
    },
    {
      accessorKey: 'customerName',
      header: 'Company',
      Cell: ({ cell }) => cell.getValue<string>() || '',
    },
  ];
};

export const Contacts: React.FC = () => {
  const { selectedJob } = useJobs();
  const [createContactOpen, setCreateContactOpen] = React.useState<{
    isOpen: boolean;
    contact: ContactDto | null;
  }>({
    isOpen: false,
    contact: null,
  });
  const { data, isLoading } = useContactsByCustomer(
    selectedJob?.customerNumber,
  );
  const contacts = data ?? [];

  const setContactOpen = (isOpen: boolean, contact: ContactDto | null) => {
    setCreateContactOpen({ isOpen, contact });
  };

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'contacts',
    columns: columns,
    defaultSorting: [{ id: 'lastName', desc: false }],
  });

  const tableExports = useTableExports<ContactDto>('job-contacts', (data) => {
    return data.map((item) => ({
      ['Job Name']: selectedJob?.name,
      ['Job Number']: selectedJob?.jobNumber,
      ['First']: item.firstName,
      ['Last']: item.lastName,
      ['Phone']: item.phone || '',
      ['Email']: item.email || '',
      ['Company']: item.customerName || '',
    }));
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 100 } },
      renderRowActions: ({ row }) => {
        const contact = row.original;
        return (
          <Group>
            <EditActionIcon onClick={() => setContactOpen(true, contact)} />
          </Group>
        );
      },
      enableClickToCopy: false,
      columns: columns,
      data: contacts,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => (
        <Group>
          <CreateButton onClick={() => setContactOpen(true, null)}>
            Create Contact
          </CreateButton>
        </Group>
      ),
    },
    getRowId: (row) => String(row.id),
    totalCount: contacts.length,
    isLoading: isLoading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  if (!selectedJob) return null;

  return (
    <>
      <Header
        breadcrumbProps={{
          items: [
            { text: 'Jobs', href: '/jobs' },
            {
              text: `${selectedJob?.name} - ${selectedJob?.jobNumber}`,
              href: `/jobs/${selectedJob?.id}/summary`,
            },
            { text: 'Contacts', href: '' },
          ],
        }}
        title={`Contacts for ${selectedJob.customerName?.trim()}`}
        subtitle="Contacts are shared across all jobs for this customer."
        helpText="View and manage contacts for this job's customer."
      />
      <CreateContactDialog
        clientOptions={
          selectedJob.customerNumber
            ? [
                {
                  custName: selectedJob.customerName,
                  custNumber: selectedJob.customerNumber,
                },
              ]
            : []
        }
        open={createContactOpen.isOpen}
        onClose={() => setContactOpen(false, null)}
        contact={createContactOpen.contact}
      />
      {table}
    </>
  );
};
